import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** API */
import { routeName } from 'routes/routes-name'

/** UTILS || SLICE */
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Grid, InputAdornment } from '@mui/material'
import ReportPermissionApi from 'api/setting/personnel-managements/report-permission.api'
import { swalActive } from 'component/Alert/Swal'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { notiError } from 'component/notifications/notifications'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import MainButton from 'new-components/buttons/main-button'
import TextInput from 'new-components/inputs/text-input'



const permissions = {
    isCreate: isCreate(),
    isDelete: isDelete(),
    isUpdate: isUpdate(),
    isView: isView()
}

export default function ReportPermission() {
    const { t } = useTranslation()
    const history = useHistory()

    const [reportPermissions, setReportPermissions] = useState([])

    /** PAGINATION  */
    const [page, setPage] = useState(1)
    const [pageLimit, setPageLimit] = useState(10)
    const [rowCount, setRowCount] = useState(0)
    const [sortBy, setSortBy] = useState('createAt')
    const [sortType, setSortType] = useState('ASC')
    const [search, setSearch] = useState('')

    const loadData = async () => {
        try {
            const resp = await ReportPermissionApi.reportsList({
                page: page,
                pageLimit: pageLimit,
                search: search,
                sortBy: sortBy,
                sortType: sortType
            })
            if (resp.status === 200) {
                setRowCount(resp.headers['x-total'])
                setReportPermissions(resp.data)
            } else {
                setReportPermissions([])
            }
        } catch (error) {
            notiError('ERROR')
        }
    }

    useEffect(() => {
        loadData()
    }, [page, pageLimit, sortBy, sortType])

    const onEdit = (objData: any) => {
        history.push(routeName.reportPermission + '/update', objData)
    }

    const onDelete = (data: any) => {
        const { roleName } = data
        swalActive(
            `<p class="mb-0 mx-auto" style="max-width: 325px">${t('REPORT_ROLE.ALERT.CONFIRM_DELETE')}</p>`,
            `<p class="mb-0 mx-auto" style="max-width: 325px">${t('REPORT_ROLE.ALERT.CONFIRM_DELETE_', {
                roleName: roleName
            })}</p>`,
            (res: any) => {
                if (res) {
                    return
                }
            }, null, t('BUTTON.SUBMIT'), t('BUTTON.CANCEL')
        )
    }

    const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }

    const handleChangePage = (val: number) => {
        setPage(val)
    }

    const handleChangeRowsPerPage = (limit: number) => {
        setPageLimit(limit)
    }

    const headCells = [
        { id: 'no', disablePadding: false, label: t('TABLE.NO'), width: '100px' },
        { id: 'roleName', disablePadding: false, label: t('REPORT_ROLE.TABLE.CELL.ROLE_NAME'), width: '40%' },
        { id: 'quantity', disablePadding: false, label: t('REPORT_ROLE.TABLE.CELL.QUANTITY_ROLE'), align: 'center' },
        { id: 'updatedBy', disablePadding: false, label: t('REPORT_ROLE.TABLE.CELL.UPDATED_BY') },
        { id: 'action', disablePadding: false, label: 'Action' }
    ]

    const renderData = (objData: any, no: number) => {
        no = page * pageLimit - pageLimit + no + 1
        const { reportPermissionId, roleName, countReport, updatedAt, updatedBy } = objData

        const objRenderData = {
            key: reportPermissionId,
            id: reportPermissionId,
            obj: objData,
            columns: [
                { option: 'TEXT', align: 'center', label: no },
                { option: 'TEXT', align: 'left', label: roleName || '-', class: 'text-ellipsis' },
                { option: 'TEXT', align: 'center', label: countReport || '-' },
                { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
                {
                    option: 'ACTION',
                    align: 'center',
                    label: 'action',
                    values: [
                        { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled }
                    ]
                }
            ]
        }
        return <TableRowCommon {...objRenderData} onedit={() => onEdit(objRenderData.obj)} ondelete={() => onDelete(objRenderData.obj)} />
    }

    return (
        <Box>
            <HeaderCard text={t('REPORT_ROLE.TITLE')} />
            <Box paddingX="24px" paddingTop="16px">
                <Grid container spacing="16px">
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <TextInput
                            label={t('REPORT_ROLE.SEARCH')}
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value)
                            }}
                            fullWidth
                            inputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" >
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('');
                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={10}>
                        <MainButton
                            title={t('INPUT.SEARCH')}
                            type='primary'
                            variant='contained'
                            style={{ width: '120px' }}
                            onClick={() => {
                                setPage(1)
                                loadData()
                            }} />
                    </Grid>
                </Grid>
                <Box paddingTop="24px">
                    <TableCustom
                        page={page}
                        pageLimit={pageLimit}
                        sortType={sortType}
                        sortBy={sortBy}
                        onSort={onRequestSort}
                        setPageLimit={handleChangeRowsPerPage}
                        setPage={handleChangePage}
                        rowCount={rowCount}
                        headCells={headCells}
                        rowsData={reportPermissions.map((val, i) => {
                            return renderData(val, i)
                        })}
                    />
                </Box>
            </Box>
        </Box>
    )
}
